/**
 * UT 사용량 비교 — 데이터 조회 (레거시 배포판)
 * 로컬 개발본은 범용 쿼리 API(genericQuery)로 SQL을 직접 조립해 실행했으나,
 * 이 배포판은 REST 엔드포인트(apis/ut/UtComparison.ts → /v1/ut/ut-comparison/*)를
 * 호출한다. analyze.ts 등 나머지 화면 로직은 이 파일이 반환하는 형태(LineInfoRow/MachineDetailRow)에만
 * 의존하므로 전혀 수정하지 않았다.
 */
import { findLineInfo, findMachineDetail, findUtMatrices } from '../../apis/ut/UtComparison';
import { MachineDetail } from '../../models/ut/UtComparison';
import { UtKey, UtMatrixOption } from './types';

export interface LineInfoRow {
  utmId: string;
  planProcId: string;
  eqclNm: string;
  eqpPrdnLnQty: number;
}

export interface MachineDetailRow {
  utmId: string;
  planProcId: string;
  utmSeq: string;
  prdnProcNm: string;
  eqclNm: string;
  dtalProcNm: string;
  prdnLnNm: string;
  eqpMainNm: string;
  machEqpNm: string;
  machEqpQty: number;
  values: Record<UtKey, { usage: number; unit: number; qty: number }>;
}

export const fetchUtMatrixList = async (): Promise<UtMatrixOption[]> => {
  const rows = await findUtMatrices();
  return rows.map((r) => ({
    utmId: r.utmId,
    utmNo: r.utmNo,
    verNo: r.verNo,
    utmNm: r.utmNm,
    statusNm: r.statusNm,
  }));
};

/**
 * 대상 UT Matrix의 상태명 보강 — ① 비교 대상 UT Matrix 표의 "상태" 칸 채우기용.
 *
 * 상태명은 TB_EELMB_UTM_RPT_UTM_M에 저장되지 않는다(UTM_ID/UTM_NM 스냅샷만 남긴다).
 * 그래서 이력 상세(getReportDetail)의 targets에는 상태가 비어 오고, UT Matrix Summary
 * 그리드에서 넘어오는 경로도 그리드 모델(UtMatrixSummaryList)에 상태 필드가 없어 비어 있다.
 * 두 경우 모두 UT Matrix 목록 API에서 utmId로 현재 상태를 메운다.
 *
 * 이미 값이 있으면 그대로 두고, 목록에 없는 대상(검토중/확정완료 외 상태는 목록에 안 나온다)은
 * 빈 값으로 남긴다. 상태명은 부가 정보라 조회에 실패해도 원본을 그대로 돌려준다.
 */
export const fillStatusNames = async (targets: UtMatrixOption[]): Promise<UtMatrixOption[]> => {
  if (targets.length === 0 || targets.every((t) => t.statusNm)) return targets;
  try {
    const list = await findUtMatrices();
    const statusByUtmId = new Map(
      (list || []).map((m) => [String(m.utmId ?? ''), String(m.statusNm ?? '')])
    );
    return targets.map((t) =>
      t.statusNm ? t : { ...t, statusNm: statusByUtmId.get(t.utmId) ?? '' }
    );
  } catch {
    return targets;
  }
};

export const fetchLineInfo = async (utmIds: string[]): Promise<LineInfoRow[]> => {
  const rows = await findLineInfo(utmIds);
  return rows.map((r) => ({
    utmId: r.utmId,
    planProcId: r.planProcId,
    eqclNm: r.eqclNm,
    eqpPrdnLnQty: r.eqpPrdnLnQty ?? 0,
  }));
};

/** UT 항목 → MachineDetail 필드 매핑 (usage=총사용량 Sum, unit=단위사용량, qty=Point/연결개수) */
const utValues = (
  r: MachineDetail
): Record<UtKey, { usage: number; unit: number; qty: number }> => ({
  CDA: { usage: r.cdaCspSumCapa ?? 0, unit: r.cdaCspCapa ?? 0, qty: r.cdaPntCnt ?? 0 },
  N2: { usage: r.nitrCspSumCapa ?? 0, unit: r.nitrCspCapa ?? 0, qty: r.nitrPntCnt ?? 0 },
  IW: { usage: r.iwCspSumCapa ?? 0, unit: r.iwCspCapa ?? 0, qty: r.iwPntCnt ?? 0 },
  PCW: { usage: r.coolCspSumCapa ?? 0, unit: r.coolCspCapa ?? 0, qty: r.coolPntCnt ?? 0 },
  Steam: { usage: r.stemCspSumCapa ?? 0, unit: r.stemCspCapa ?? 0, qty: r.stemPntCnt ?? 0 },
  NG: { usage: r.lngCspSumCapa ?? 0, unit: r.lngCspCapa ?? 0, qty: r.lngPntCnt ?? 0 },
  Return: {
    usage: r.insdExhaCspSumCapa ?? 0,
    unit: r.insdExhaCspCapa ?? 0,
    qty: r.insdExhaPntCnt ?? 0,
  },
  Exhaust: {
    usage: r.otsdExhaCspSumCapa ?? 0,
    unit: r.otsdExhaCspCapa ?? 0,
    qty: r.otsdExhaPntCnt ?? 0,
  },
  Supply: { usage: r.suarCspSumCapa ?? 0, unit: r.suarCspCapa ?? 0, qty: r.suarCnctCnt ?? 0 },
});

export const fetchMachineDetail = async (utmIds: string[]): Promise<MachineDetailRow[]> => {
  const rows = await findMachineDetail(utmIds);
  return rows.map((r) => ({
    utmId: r.utmId,
    planProcId: r.planProcId,
    utmSeq: String(r.utmSeq ?? ''),
    prdnProcNm: r.prdnProcNm ?? '',
    eqclNm: r.eqclNm ?? '',
    dtalProcNm: r.dtalProcNm ?? '',
    prdnLnNm: r.prdnLnNm ?? '',
    eqpMainNm: r.eqpMainNm ?? '',
    machEqpNm: r.eqpMchNm ?? '',
    machEqpQty: r.machEqpQty ?? 0,
    values: utValues(r),
  }));
};

export const errorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : String(err);
